import moment from 'moment';

/**
 * BOPS-175 — the Daily Notes and Billing page's note, in the shape the Hold from billing and
 * Archive hooks expect.
 *
 * Both hooks were written against a row of the Episode of Care completed-documents list, and
 * BOPS-175 is explicit that this page reuses them rather than growing a second variant of the
 * same two actions. The two surfaces hold the same note under different field names, so the
 * translation happens here — once, in one place — instead of loosening every predicate in
 * `hold-from-billing.tsx` and `archive-note.tsx` to accept either shape.
 */
export type NoteDocument = {
  dailySessionNoteUuid?: string;
  formType: string;
  dateOfService: string;
  pdfUrl?: string;
  heldFromBilling?: boolean;
  /** THIN-8050: MANUAL or SYSTEM when held (THIN-8044); null/absent when not held. */
  holdOrigin?: 'MANUAL' | 'SYSTEM' | null;
  archived?: boolean;
  signatureContext?: { therapist?: { name?: string } };
};

type NoteDocumentInput = {
  /** The DSN uuid the page already derives for Delete — never the form-submission uuid. */
  dsnUuid?: string;
  /** The row the page was opened from, i.e. one entry of `/provider/v2/daily-notes/all`. */
  selectedPatient?: any;
  /** The date being viewed. The page's own picker, not a field on the note. */
  selectedDate?: any;
  /** Present once the note has been signed and a document produced. */
  pdfUrl?: string;
  /** Signed-in user, the same value the delete dialog on this page shows. */
  therapistName?: string;
};

/**
 * `isDailySessionNote` gates both hooks on `formType` containing DAILY_SOAP_NOTE. The daily-notes
 * row has no formType at all — it doesn't need one, because the page shows nothing else — so it
 * is asserted rather than read. Every other field is passed through as-is so an absent one fails
 * the hooks' own guards instead of being invented here.
 */
export const toNoteDocument = ({
  dsnUuid,
  selectedPatient,
  selectedDate,
  pdfUrl,
  therapistName,
}: NoteDocumentInput): NoteDocument => ({
  dailySessionNoteUuid: dsnUuid,
  formType: 'DAILY_SOAP_NOTE',
  //  Sent as a plain calendar day: the hooks' closed-month check and the confirmation dialog's
  //  date line both read it the way the documents list writes it, and a Date carrying a local
  //  midnight would land on the previous day once they correct for the timezone.
  dateOfService: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
  pdfUrl,
  //  BOPS-175 open question: `/provider/v2/daily-notes/all` carries neither flag today, so on
  //  first load both read as false and the badges only appear once the user acts. Read off the
  //  row anyway — the day the backend adds them, the badges light up with no change here.
  heldFromBilling: selectedPatient?.heldFromBilling === true,
  holdOrigin: selectedPatient?.holdOrigin ?? null,
  archived: selectedPatient?.archived === true,
  signatureContext: { therapist: { name: therapistName } },
});
